/**
 * FERP Portal - Generic Primitive Data Hook
 *
 * Provides data loading for all 25 FERP primitives using the correct GraphQL query.
 * Maps primitive type → GraphQL hook dynamically.
 *
 * PATTERN: DRY - ONE hook for all primitives
 *
 * MIGRATION: The 25-way switch in usePrimitiveQuery and extractItems exists because
 * each primitive has its own generated GraphQL query hook. Replace with a unified
 * `listPrimitives(type: PrimitiveType, limit: Int, cursor: String)` backend query
 * returning `PrimitiveConnection { items: [JSON!]!, totalCount, hasNextPage, cursor }`
 * when the backend supports it.
 *
 * Known inconsistencies in extractItems (response shapes that differ from `.items`):
 * - listMyWorkflows: flat array (no .items wrapper)
 * - listTeams: flat array
 * - listValidations: flat array
 * - listCheckpointsByWorkflow: flat array
 * - listDocuments: .nodes (not .items)
 * - listAnalytics: .nodes (not .items)
 * - listIntegrations: .nodes (not .items)
 * - listTransactionsByTenant: .transactions (not .items)
 * - listAudits: .audits (not .items)
 */

import { useTenantId } from '@/auth';
import { useDevActorId } from './useDevActorId';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import {
  useListWorkflowsQuery,
  useListMyWorkflowsQuery,
  useListTemplatesQuery,
  useListDependenciesByWorkflowQuery,
  useListPoliciesQuery,
  useListRolesQuery,
  useListTeamsQuery,
  useListActorsQuery,
  useListValidationsQuery,
  useListConstraintsQuery,
  useListInstancesQuery,
  useListTasksQuery,
  useListQueuesQuery,
  useListDocumentsQuery,
  useListEventsQuery,
  useListTracesByWorkflowInstanceQuery,
  useListCheckpointsByWorkflowQuery,
  useListAuditsQuery,
  useListSchedulesQuery,
  useListContractsByStatusQuery,
  useListApprovalsByStatusQuery,
  useListTransactionsByTenantQuery,
  useListAnalyticsQuery,
  useListResourcesQuery,
  useListIntegrationsQuery,
  GqlContractStatus,
  GqlApprovalStatus,
} from '@/generated/graphql';
import type { PrimitiveType } from '@/config/primitive-metadata';

// Inline query for tenant-wide notification list (pending codegen for listNotificationsByTenant)
const LIST_NOTIFICATIONS_BY_TENANT = gql`
  query ListNotificationsByTenant($limit: Int) {
    listNotificationsByTenant(limit: $limit) {
      items {
        id
        notificationTitle
        messageTemplate
        notificationType
        priority
        channels
        status
        createdAt
      }
      hasMore
      nextCursor
    }
  }
`;

/**
 * Generic primitive item type - common fields across all primitives
 */
export interface PrimitiveItem {
  id: string;
  name: string;
  description?: string | null;
  status?: string | null;
  active?: boolean | null;
  enabled?: boolean | null;
  safetyLevel?: string | null;
  updatedAt?: string | null;
  createdAt?: string | null;
  // Additional fields specific to each primitive
  [key: string]: any;
}

/**
 * Hook result type
 */
export interface UsePrimitiveDataResult {
  items: PrimitiveItem[];
  loading: boolean;
  error: any;
  refetch: () => void;
}

/**
 * Generic hook for loading primitive data
 *
 * Usage:
 * ```tsx
 * const { items, loading, error, refetch } = usePrimitiveData('policy');
 * ```
 */
export function usePrimitiveData(primitiveType: PrimitiveType): UsePrimitiveDataResult {
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;
  // DRR-102: Workflow list is actor-scoped — backend filters by role.
  const { actorId } = useDevActorId();

  // Common query options
  const queryOptions = {
    variables: { tenantId, limit: 100 },
    skip,
    errorPolicy: 'all' as const,
  };

  // Map primitive type to GraphQL hook
  const queryResult = usePrimitiveQuery(primitiveType, queryOptions, actorId);

  // Extract items from the query result
  const items = extractItems(primitiveType, queryResult.data);

  return {
    items,
    loading: queryResult.loading,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
}

/**
 * Maps primitive type to the correct GraphQL query hook
 */
function usePrimitiveQuery(primitiveType: PrimitiveType, options: any, actorId?: string | null) {
  // Approval requires all 3 statuses. Call unconditionally (with skip) to satisfy hooks rules.
  const approvalSkip = options.skip || primitiveType !== 'approval';
  const approvalPending = useListApprovalsByStatusQuery({
    ...options,
    variables: { tenantId: options.variables?.tenantId ?? '', status: GqlApprovalStatus.PENDING, limit: 100 },
    skip: approvalSkip,
  });
  const approvalInReview = useListApprovalsByStatusQuery({
    ...options,
    variables: { tenantId: options.variables?.tenantId ?? '', status: GqlApprovalStatus.IN_REVIEW, limit: 100 },
    skip: approvalSkip,
  });
  const approvalApproved = useListApprovalsByStatusQuery({
    ...options,
    variables: { tenantId: options.variables?.tenantId ?? '', status: GqlApprovalStatus.APPROVED, limit: 100 },
    skip: approvalSkip,
  });

  // DRR-102: Workflows are role-filtered via listMyWorkflows when actorId is known.
  // Falls back to listWorkflows (unfiltered) only while actorId is still resolving.
  const workflowQuery = useListMyWorkflowsQuery({
    variables: { actorId: actorId ?? '' },
    skip: options.skip || !actorId,
    errorPolicy: 'all' as const,
  });
  // Fallback while actor resolves (prevents empty flash)
  const workflowFallback = useListWorkflowsQuery({
    ...options,
    skip: options.skip || !!actorId,
  });

  switch (primitiveType) {
    // DESIGN Phase
    case 'workflow':
      return actorId ? workflowQuery : workflowFallback;
    case 'template':
      return useListTemplatesQuery(options);
    case 'dependency':
      // Dependencies are workflow-instance-specific — no standalone list query.
      // Skip so the list view shows empty state rather than querying with empty workflowId.
      return useListDependenciesByWorkflowQuery({
        ...options,
        variables: { ...options.variables, workflowId: '' },
        skip: true,
      });
    case 'policy':
      return useListPoliciesQuery(options);
    case 'role':
      return useListRolesQuery(options);

    // CONFIGURE Phase
    case 'team':
      return useListTeamsQuery(options);
    case 'actor':
      return useListActorsQuery(options);
    case 'validation':
      return useListValidationsQuery(options);
    case 'contract':
      return useListContractsByStatusQuery({
        ...options,
        variables: { status: GqlContractStatus.ACTIVE, limit: 100 },
      });
    case 'constraint':
      return useListConstraintsQuery(options);

    // EXECUTE Phase
    case 'instance':
      return useListInstancesQuery(options);
    case 'task':
      return useListTasksQuery(options);
    case 'approval': {
      const mergedItems = [
        ...(approvalPending.data?.listApprovalsByStatus?.items ?? []),
        ...(approvalInReview.data?.listApprovalsByStatus?.items ?? []),
        ...(approvalApproved.data?.listApprovalsByStatus?.items ?? []),
      ];
      return {
        data: mergedItems.length > 0 || approvalPending.data || approvalInReview.data || approvalApproved.data
          ? { listApprovalsByStatus: { items: mergedItems } }
          : null,
        loading: approvalPending.loading || approvalInReview.loading || approvalApproved.loading,
        error: approvalPending.error ?? approvalInReview.error ?? approvalApproved.error,
        refetch: approvalPending.refetch,
        networkStatus: approvalPending.networkStatus,
      };
    }
    case 'document':
      return useListDocumentsQuery(options);
    case 'queue':
      return useListQueuesQuery(options);

    // MONITOR Phase
    case 'event':
      return useListEventsQuery(options);
    case 'notification':
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useQuery(LIST_NOTIFICATIONS_BY_TENANT, {
        ...options,
        variables: { limit: 100 },
      });
    case 'trace':
      // Traces are workflow-instance-specific — skip standalone list (no global trace query).
      return useListTracesByWorkflowInstanceQuery({
        ...options,
        variables: { ...options.variables, workflowInstanceId: '' },
        skip: true,
      });
    case 'checkpoint':
      // Checkpoints are workflow-instance-specific — skip standalone list.
      return useListCheckpointsByWorkflowQuery({
        ...options,
        variables: { ...options.variables, workflowInstanceId: '' },
        skip: true,
      });
    case 'transaction':
      return useListTransactionsByTenantQuery(options);

    // OPTIMIZE Phase
    case 'audit':
      return useListAuditsQuery(options);
    case 'analytics':
      return useListAnalyticsQuery(options);
    case 'schedule':
      return useListSchedulesQuery(options);
    case 'resource':
      return useListResourcesQuery(options);
    case 'integration':
      return useListIntegrationsQuery(options);

    default:
      return { data: null, loading: false, error: null, refetch: () => {} };
  }
}

/**
 * Extracts items array from GraphQL query result
 */
function extractItems(primitiveType: PrimitiveType, data: any): PrimitiveItem[] {
  if (!data) return [];

  switch (primitiveType) {
    // DESIGN Phase
    case 'workflow':
      // listMyWorkflows returns a flat array; listWorkflows returns paginated items
      return data.listMyWorkflows ?? data.listWorkflows?.items ?? [];
    case 'template':
      return data.listTemplates?.items ?? [];
    case 'dependency':
      return data.listDependenciesByWorkflow?.items ?? [];
    case 'policy':
      return data.listPolicies?.items ?? [];
    case 'role':
      return data.listRoles?.items ?? [];

    // CONFIGURE Phase
    case 'team':
      return data.listTeams ?? [];
    case 'actor':
      return data.listActors?.items ?? [];
    case 'validation':
      return data.listValidations ?? [];
    case 'contract':
      return data.listContractsByStatus?.items ?? [];
    case 'constraint':
      return data.listConstraints?.items ?? [];

    // EXECUTE Phase
    case 'instance': {
      // Instance GraphQL type uses `instanceName` not `name` — map to PrimitiveItem.name.
      const mapped = (data.listInstances?.items ?? []).map((item: any) => ({
        ...item,
        name: item.name ?? item.instanceName ?? item.workflowName ?? `Instance ${item.id?.slice?.(-8) ?? ''}`,
        description: item.description ?? item.workflowDescription ?? undefined,
      }));
      // Append-only dedup: group by (workflowDefinitionId + instanceName), keep highest version.
      const best = new Map<string, any>();
      for (const item of mapped) {
        const key = `${item.workflowDefinitionId ?? ''}:${item.instanceName ?? item.id}`;
        const existing = best.get(key);
        if (!existing || (item.version ?? 0) > (existing.version ?? 0)) {
          best.set(key, item);
        }
      }
      return Array.from(best.values());
    }
    case 'task':
      return data.taskApiListTasks?.items ?? [];
    case 'approval':
      return data.listApprovalsByStatus?.items ?? [];
    case 'document':
      return data.listDocuments?.nodes ?? [];
    case 'queue':
      return data.listQueues?.items ?? [];

    // MONITOR Phase
    case 'event':
      return data.listEvents?.items ?? [];
    case 'notification':
      return data.listNotificationsByTenant?.items ?? [];
    case 'trace':
      return data.listTracesByWorkflowInstance?.items ?? [];
    case 'checkpoint':
      return data.listCheckpointsByWorkflow ?? [];
    case 'transaction':
      return data.listTransactionsByTenant?.transactions ?? [];

    // OPTIMIZE Phase
    case 'audit':
      return data.listAudits?.audits ?? [];
    case 'analytics':
      return data.listAnalytics?.nodes ?? [];
    case 'schedule':
      return data.listSchedules?.items ?? [];
    case 'resource':
      return data.listResources?.items ?? [];
    case 'integration':
      return data.listIntegrations?.nodes ?? [];

    default:
      return [];
  }
}
